import React, { useEffect, useState } from 'react';
import logoImg from '../image/logo.png';
import Sidebar from './Sidebar';
import Loader from './Loader';
import { toast } from 'react-toastify';
import { firestore } from '../firebase.config';
import { collection, getDocs, deleteDoc, doc, updateDoc } from 'firebase/firestore';
import { Trash2, FileText, ChevronLeft, ChevronRight, RefreshCw, Search, SlidersHorizontal, X, TrendingUp, Users, IndianRupee, Heart } from 'lucide-react';
import jsPDF from 'jspdf';
import { FaWhatsapp } from 'react-icons/fa';

const cloudName = "dabhatz7m";
const uploadPreset = "ImageUpload";
const ITEMS_PER_PAGE = 5;

/* ─── Inline styles / CSS ─── */
const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700;800&family=JetBrains+Mono:wght@400;500&display=swap');

  .dn-root * { font-family: 'Sora', sans-serif; box-sizing: border-box; }

  .dn-root {
    --gold:      #fcaf17;
    --gold-dark: #e09800;
    --gold-pale: #fff8e7;
    --gold-soft: #fef3c7;
    --amber:     #f59e0b;
    --orange:    #fb923c;
    --ink:       #1a1208;
    --muted:     #78716c;
    --border:    #fde68a;
    --surface:   #fffdf7;
    --white:     #ffffff;
    --danger:    #ef4444;
    --success:   #22c55e;
  }

  /* stat cards */
  .dn-stat-card {
    background: var(--white);
    border: 1.5px solid var(--border);
    border-radius: 16px;
    padding: 20px 24px;
    position: relative;
    overflow: hidden;
    transition: transform .2s, box-shadow .2s;
  }
  .dn-stat-card:hover { transform: translateY(-2px); box-shadow: 0 8px 24px rgba(252,175,23,.18); }
  .dn-stat-card .accent-bar {
    position: absolute; top: 0; left: 0; height: 4px; width: 100%;
    background: linear-gradient(90deg, var(--gold), var(--orange));
    border-radius: 16px 16px 0 0;
  }

  /* filter panel */
  .dn-filter-panel {
    background: var(--white);
    border: 1.5px solid var(--border);
    border-radius: 16px;
    padding: 20px;
    margin-bottom: 24px;
  }

  /* inputs / selects */
  .dn-input, .dn-select {
    width: 100%;
    padding: 10px 14px;
    border: 1.5px solid #e5e0d5;
    border-radius: 10px;
    font-size: 13px;
    color: var(--ink);
    background: var(--surface);
    outline: none;
    transition: border-color .2s, box-shadow .2s;
    font-family: 'Sora', sans-serif;
  }
  .dn-input:focus, .dn-select:focus {
    border-color: var(--gold);
    box-shadow: 0 0 0 3px rgba(252,175,23,.15);
  }
  .dn-input::placeholder { color: #c4b89a; }

  /* table */
  .dn-table-wrap {
    border: 1.5px solid var(--border);
    border-radius: 16px;
    overflow: hidden;
    background: var(--white);
  }
  .dn-table { width: 100%; border-collapse: collapse; min-width: 1500px; font-size: 13px; }
  .dn-table thead tr {
    background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
  }
  .dn-table thead th {
    padding: 14px 18px;
    font-weight: 700;
    font-size: 11px;
    letter-spacing: .06em;
    text-transform: uppercase;
    color: #92400e;
    white-space: nowrap;
    border-bottom: 2px solid var(--border);
  }
  .dn-table tbody tr {
    border-bottom: 1px solid #fef3c7;
    transition: background .15s;
  }
  .dn-table tbody tr:hover { background: var(--gold-pale); }
  .dn-table tbody tr:last-child { border-bottom: none; }
  .dn-table td { padding: 13px 18px; color: var(--ink); white-space: nowrap; vertical-align: middle; }

  /* sticky columns */
  .col-sticky-left {
    position: sticky; left: 0; z-index: 10;
    background: var(--white);
    box-shadow: 3px 0 8px -2px rgba(0,0,0,.08);
  }
  .dn-table thead .col-sticky-left { background: #fde68a; z-index: 40; }
  .dn-table tbody tr:hover .col-sticky-left { background: var(--gold-pale); }

  /* action buttons */
  .dn-action-btn {
    width: 34px; height: 34px;
    border-radius: 10px;
    display: inline-flex; align-items: center; justify-content: center;
    border: none; cursor: pointer;
    transition: all .2s;
    flex-shrink: 0;
  }
  .dn-action-btn.whatsapp { background: #dcfce7; color: #16a34a; }
  .dn-action-btn.whatsapp:hover { background: #16a34a; color: #fff; transform: scale(1.08); }
  .dn-action-btn.receipt  { background: #dbeafe; color: #2563eb; }
  .dn-action-btn.receipt:hover  { background: #2563eb; color: #fff; transform: scale(1.08); }
  .dn-action-btn.delete   { background: #fee2e2; color: #dc2626; }
  .dn-action-btn.delete:hover   { background: #dc2626; color: #fff; transform: scale(1.08); }

  /* badge */
  .dn-badge {
    display: inline-block;
    padding: 3px 10px;
    border-radius: 20px;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: .03em;
  }
  .dn-badge.yes  { background: #dcfce7; color: #15803d; }
  .dn-badge.no   { background: #fee2e2; color: #b91c1c; }
  .dn-badge.monthly { background: var(--gold-soft); color: #92400e; }
  .dn-badge.onetime { background: #f0f9ff; color: #0369a1; }

  /* chip */
  .dn-chip {
    display: inline-flex; align-items: center; gap: 6px;
    background: var(--gold-pale);
    border: 1.5px solid #fde68a;
    color: #92400e;
    font-size: 11.5px;
    font-weight: 600;
    padding: 4px 10px;
    border-radius: 20px;
  }
  .dn-chip .chip-label { color: var(--amber); }
  .dn-chip button { background: none; border: none; cursor: pointer; color: #c4843a; display: flex; align-items: center; transition: color .15s; padding: 0; }
  .dn-chip button:hover { color: var(--danger); }

  /* pagination */
  .dn-page-btn {
    min-width: 36px; height: 36px; padding: 0 10px;
    display: inline-flex; align-items: center; justify-content: center; gap: 4px;
    border: 1.5px solid #e5e0d5;
    border-radius: 9px;
    font-size: 13px; font-weight: 600;
    color: var(--muted);
    background: var(--white);
    cursor: pointer;
    transition: all .18s;
  }
  .dn-page-btn:hover:not(:disabled) { border-color: var(--gold); color: var(--gold-dark); background: var(--gold-pale); }
  .dn-page-btn.active { background: var(--gold); border-color: var(--gold); color: var(--white); box-shadow: 0 3px 10px rgba(252,175,23,.4); }
  .dn-page-btn:disabled { opacity: .4; cursor: not-allowed; }

  /* clear btn */
  .dn-clear-btn {
    display: flex; align-items: center; justify-content: center; gap: 7px;
    padding: 10px 18px;
    border-radius: 10px;
    font-size: 13px; font-weight: 700;
    cursor: pointer; border: none;
    transition: all .2s;
    font-family: 'Sora', sans-serif;
    white-space: nowrap;
  }
  .dn-clear-btn.active { background: linear-gradient(135deg, var(--gold), var(--amber)); color: var(--white); box-shadow: 0 4px 14px rgba(252,175,23,.45); }
  .dn-clear-btn.inactive { background: var(--white); border: 1.5px solid #e5e0d5; color: var(--muted); }
  .dn-clear-btn.inactive:hover { border-color: var(--gold); color: var(--gold-dark); }

  /* empty state */
  .dn-empty { text-align: center; padding: 64px 24px; color: var(--muted); }
  .dn-empty .dn-empty-icon { font-size: 48px; margin-bottom: 16px; opacity: .5; }

  /* section label */
  .dn-label {
    font-size: 11px; font-weight: 700; letter-spacing: .08em;
    text-transform: uppercase; color: #a8927a; margin-bottom: 7px;
  }

  /* amount cell */
  .amount-cell { font-family: 'JetBrains Mono', monospace; font-weight: 500; font-size: 13px; }
`;

/* ─── Stat Card ─── */
const StatCard = ({ icon: Icon, label, value, sub, color }) => (
  <div className="dn-stat-card">
    <div className="accent-bar" />
    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
      <div>
        <p style={{ fontSize: 12, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', color: '#a8927a', marginBottom: 8 }}>{label}</p>
        <p style={{ fontSize: 26, fontWeight: 800, color: '#1a1208', lineHeight: 1 }}>{value}</p>
        {sub && <p style={{ fontSize: 12, color: '#78716c', marginTop: 6 }}>{sub}</p>}
      </div>
      <div style={{ width: 44, height: 44, borderRadius: 12, background: color, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <Icon size={20} color="#fff" />
      </div>
    </div>
  </div>
);

/* ─── Main Component ─── */
const DonateNow = () => {
  const [donateList, setDonateList]       = useState([]);
  const [isLoading, setIsLoading]         = useState(true);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [currentPage, setCurrentPage]     = useState(1);
  const [showFilters, setShowFilters]     = useState(true);

  const [filterName,         setFilterName]         = useState('');
  const [filterMobile,       setFilterMobile]       = useState('');
  const [filterDate,         setFilterDate]         = useState('');
  const [filterCategory,     setFilterCategory]     = useState('');
  const [filterProduct,      setFilterProduct]      = useState('');
  const [filterDonationKind, setFilterDonationKind] = useState('');
  const [filterDonationType, setFilterDonationType] = useState('');

  const goshalaDetails = {
    name: "Madan Mohan Goshala",
    registration: "(Registered under Section 12A & 80G of Income Tax Act)",
    address: "CG3G+54H, Moulabhanaja, Chanchadapalli, Odisha 761014",
    phone: "+91 79781 37498",
    email: "Madanmohangoshala@gmail.com",
    website: "https://madanmohangoshala.com/",
    goshala80GApprovalNo: "Approval No from IT Department",
    goshala80GApprovalDate: "01/04/2023",
    goshala80GValidUntil: "31/03/2028",
    authorizedSignatoryName: "Name of Trustee / President",
    authorizedSignatoryDesignation: "Designation",
  };

  useEffect(() => { fetchDonations(); }, []);

  const fetchDonations = async () => {
    setIsLoading(true);
    try {
      const querySnapshot = await getDocs(collection(firestore, 'DonateNow'));
      const data = querySnapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      setDonateList(data);
    } catch (err) {
      toast.error("Error loading donations");
    } finally {
      setIsLoading(false);
    }
  };

  const uniqueValues = (key) => [...new Set(donateList.map(d => d[key]).filter(Boolean))].sort();

  const handleRefresh = () => {
    setFilterName(''); setFilterMobile(''); setFilterDate('');
    setFilterCategory(''); setFilterProduct('');
    setFilterDonationKind(''); setFilterDonationType('');
    setCurrentPage(1);
  };

  const handleDeleteDonation = async (id) => {
    if (window.confirm("Delete this donation?")) {
      try {
        await deleteDoc(doc(firestore, 'DonateNow', id));
        setDonateList(prev => prev.filter(item => item.id !== id));
        toast.success("Deleted successfully");
      } catch { toast.error("Failed to delete donation record."); }
    }
  };

  const handleWhatsAppMessage = (mobileNumber, fullName, donationAmount, item) => {
    if (!mobileNumber) { toast.error("Mobile number missing"); return; }
    let cleanNumber = mobileNumber.replace(/\D/g, "");
    if (!cleanNumber.startsWith("91")) cleanNumber = "91" + cleanNumber;
    let donationDetails = "";
    if (item.donationType === "monthly") {
      const monthlyDonations = donateList.filter(d => d.mobileNumber === mobileNumber && d.donationType === "monthly");
      donationDetails = monthlyDonations.map((d, i) => {
        const date = d.sevaDate ? new Date(d.sevaDate).toLocaleDateString("en-GB") : "N/A";
        return `• Month ${i + 1} : ₹${d.donationAmount} (${date})`;
      }).join("\n");
    } else {
      const date = item.sevaDate ? new Date(item.sevaDate).toLocaleDateString("en-GB") : "N/A";
      donationDetails = `• Amount : ₹${donationAmount}\n• Date : ${date}`;
    }
    const message = encodeURIComponent(`🙏 Dear ${fullName},\n\nThank you for your generous support to ${goshalaDetails.name}.\n\nDonation Details:\n${donationDetails}\n\nPAN: ${item.panNumber || "N/A"}\n\nRegards,\n${goshalaDetails.authorizedSignatoryName}`);
    window.open(`https://wa.me/${cleanNumber}?text=${message}`, "_blank");
  };

  const convertNumberToWords = (num) => {
    const units = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine'];
    const teens = ['Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
    const tens  = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
    if (num === 0) return 'Zero';
    let words = '';
    if (Math.floor(num / 10000000) > 0) { words += convertNumberToWords(Math.floor(num / 10000000)) + ' Crore '; num %= 10000000; }
    if (Math.floor(num / 100000)   > 0) { words += convertNumberToWords(Math.floor(num / 100000))   + ' Lakh ';  num %= 100000;  }
    if (Math.floor(num / 1000)     > 0) { words += convertNumberToWords(Math.floor(num / 1000))     + ' Thousand '; num %= 1000; }
    if (Math.floor(num / 100)      > 0) { words += convertNumberToWords(Math.floor(num / 100))      + ' Hundred '; num %= 100;  }
    if (num > 0) {
      if (words !== '') words += 'and ';
      if (num < 10)      words += units[num];
      else if (num < 20) words += teens[num - 10];
      else { words += tens[Math.floor(num / 10)]; if (num % 10 > 0) words += ' ' + units[num % 10]; }
    }
    return words.trim();
  };

  const generateReceiptPdf = async (item) => {
    let rawAmount = item.donationAmount;
    let amount = typeof rawAmount === 'string' ? (parseInt(rawAmount.replace(/[₹,]/g, '')) || 0) : (rawAmount || 0);
    if (amount < 500) { toast.error("Receipt allowed only for donations ₹500 or more"); return; }

    const donationDate  = item.sevaDate ? new Date(item.sevaDate).toLocaleDateString('en-GB') : 'N/A';
    const receiptDate   = new Date().toLocaleDateString('en-GB');
    const receiptNo     = `R${Date.now()}`;
    const amountInWords = convertNumberToWords(amount);

    const pdf = new jsPDF('p', 'mm', 'a4');
    const W = 210;
    const ML = 14; // margin left
    const MR = 14; // margin right
    const TW = W - ML - MR; // total content width = 182mm

    // ── helpers ──
    const black  = [0, 0, 0];
    const dkgray = [50, 50, 50];
    const mdgray = [100, 100, 100];
    const ltgray = [180, 180, 180];
    const vltgray= [240, 240, 240];
    const white  = [255, 255, 255];

    const setBlack  = () => { pdf.setTextColor(...black);  pdf.setDrawColor(...black);  };
    const setDkGray = () => { pdf.setTextColor(...dkgray); };
    const setMdGray = () => { pdf.setTextColor(...mdgray); };
    const hline = (y, lw = 0.3) => {
      pdf.setDrawColor(...ltgray); pdf.setLineWidth(lw);
      pdf.line(ML, y, W - MR, y);
    };
    const box = (x, y, w, h, fill = false) => {
      if (fill) { pdf.setFillColor(...vltgray); pdf.rect(x, y, w, h, 'F'); }
      pdf.setDrawColor(...ltgray); pdf.setLineWidth(0.3);
      pdf.rect(x, y, w, h, 'S');
    };

    // ══════════════════════════════
    //  HEADER
    // ══════════════════════════════
    let y = ML;

    // Outer border for whole receipt
    pdf.setDrawColor(...black); pdf.setLineWidth(0.5);
    pdf.rect(ML, ML, TW, 269, 'S');

    // Logo (left side of header)
    try {
      const logoImage = new Image();
      logoImage.src = logoImg;
      await new Promise((resolve) => { logoImage.onload = resolve; logoImage.onerror = resolve; });
      const canvas = document.createElement('canvas');
      canvas.width = logoImage.naturalWidth || 120;
      canvas.height = logoImage.naturalHeight || 120;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(logoImage, 0, 0);
      const logoDataUrl = canvas.toDataURL('image/png');
      pdf.addImage(logoDataUrl, 'PNG', ML + 2, y + 2, 20, 20);
    } catch (e) {
      console.warn('Logo load failed:', e);
    }

    // Org name
    setBlack();
    pdf.setFontSize(14); pdf.setFont('helvetica', 'bold');
    pdf.text(goshalaDetails.name, W / 2, y + 8, { align: 'center' });

    pdf.setFontSize(8); pdf.setFont('helvetica', 'normal'); setMdGray();
    pdf.text(goshalaDetails.registration, W / 2, y + 14, { align: 'center' });
    pdf.text(goshalaDetails.address, W / 2, y + 19, { align: 'center' });
    pdf.text(`Ph: ${goshalaDetails.phone}   Email: ${goshalaDetails.email}   Web: ${goshalaDetails.website}`, W / 2, y + 24, { align: 'center' });

    hline(y + 28, 0.5);

    // "DONATION RECEIPT" centered title
    setBlack();
    pdf.setFontSize(11); pdf.setFont('helvetica', 'bold');
    pdf.text('DONATION RECEIPT', W / 2, y + 35, { align: 'center' });

    hline(y + 39, 0.5);

    // Receipt No | Date row  (grey background)
    pdf.setFillColor(...vltgray);
    pdf.rect(ML, y + 39, TW, 9, 'F');
    pdf.setFontSize(8.5); pdf.setFont('helvetica', 'bold'); setBlack();
    pdf.text(`Receipt No: ${receiptNo}`, ML + 4, y + 45);
    pdf.text(`Date: ${receiptDate}`, W - MR - 4, y + 45, { align: 'right' });

    hline(y + 48, 0.3);
    y += 52;

    // ══════════════════════════════
    //  TABLE HELPER
    //  drawTable(y, title, rows)
    //  rows = [ [label, value, label, value], ... ]
    //  Each row has 4 cells: col1-label, col1-value, col2-label, col2-value
    // ══════════════════════════════
    const COL1_X = ML;           // left column starts at left margin
    const COL2_X = ML + TW / 2; // right column starts at midpoint
    const CELL_PAD = 3;
    const ROW_H = 12;            // height per data row
    const HDR_H = 8;             // height of section header row

    const drawTable = (startY, title, rows) => {
      let ty = startY;

      // Section header row (dark fill)
      pdf.setFillColor(...dkgray);
      pdf.rect(ML, ty, TW, HDR_H, 'F');
      pdf.setTextColor(...white);
      pdf.setFontSize(8.5); pdf.setFont('helvetica', 'bold');
      pdf.text(title, ML + CELL_PAD, ty + 5.5);
      ty += HDR_H;

      rows.forEach((row, i) => {
        const [lbl1, val1, lbl2, val2] = row;
        const rowY = ty + i * ROW_H;

        // Alternating light fill on even rows
        if (i % 2 === 0) {
          pdf.setFillColor(248, 248, 248);
          pdf.rect(ML, rowY, TW, ROW_H, 'F');
        }

        // Vertical centre divider (only when right cell exists)
        if (lbl2 !== undefined) {
          pdf.setDrawColor(...ltgray); pdf.setLineWidth(0.25);
          pdf.line(COL2_X, rowY, COL2_X, rowY + ROW_H);
        }

        // Bottom row divider
        pdf.setDrawColor(...ltgray); pdf.setLineWidth(0.25);
        pdf.line(ML, rowY + ROW_H, W - MR, rowY + ROW_H);

        // Left cell
        pdf.setFontSize(7); pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(...mdgray);
        pdf.text(String(lbl1 || ''), COL1_X + CELL_PAD, rowY + CELL_PAD + 2);

        pdf.setFontSize(9); pdf.setFont('helvetica', 'normal');
        setBlack();
        pdf.text(String(val1 || 'N/A'), COL1_X + CELL_PAD, rowY + CELL_PAD + 8);

        // Right cell
        if (lbl2 !== undefined) {
          pdf.setFontSize(7); pdf.setFont('helvetica', 'bold');
          pdf.setTextColor(...mdgray);
          pdf.text(String(lbl2 || ''), COL2_X + CELL_PAD, rowY + CELL_PAD + 2);

          pdf.setFontSize(9); pdf.setFont('helvetica', 'normal');
          setBlack();
          pdf.text(String(val2 || 'N/A'), COL2_X + CELL_PAD, rowY + CELL_PAD + 8);
        }
      });

      // Outer border around whole table
      pdf.setDrawColor(...black); pdf.setLineWidth(0.3);
      pdf.rect(ML, startY, TW, HDR_H + rows.length * ROW_H, 'S');

      return startY + HDR_H + rows.length * ROW_H;
    };

    // ══════════════════════════════
    //  DONOR DETAILS TABLE
    // ══════════════════════════════
    y = drawTable(y, 'DONOR DETAILS', [
      ['Full Name',   item.fullName      || 'N/A', 'Mobile',     item.mobileNumber  || 'N/A'],
      ['Email',       item.emailAddress  || 'N/A', 'PAN Number', item.panNumber     || 'N/A'],
      ['State',       item.state         || 'N/A', 'Address',    item.address       || 'N/A'],
    ]);

    y += 6;

    // ══════════════════════════════
    //  DONATION DETAILS TABLE
    // ══════════════════════════════
    const is80G = parseInt(item.donationAmount) >= 500 && item.receive80GCertificate;
const hasCategory =
  item.category &&
  item.category.trim() !== '' &&
  item.category.trim().toLowerCase() !== 'n/a';

const hasDonationKind =
  item.donationKind &&
  item.donationKind.trim() !== '' &&
  item.donationKind.trim().toLowerCase() !== 'n/a';
  let categoryRow;

if (hasCategory && hasDonationKind) {
  categoryRow = ['Category', item.category, 'Donation Kind', item.donationKind];
} else if (hasCategory) {
  categoryRow = ['Category', item.category, undefined, undefined];
} else if (hasDonationKind) {
  categoryRow = ['Donation Kind', item.donationKind, undefined, undefined];
} else {
  categoryRow = null;
}
    const donationRows = [
      ...(categoryRow ? [categoryRow] : []),
      ['Donation Type', item.donationType === 'monthly' ? 'Monthly' : 'One Time', 'Seva Date', donationDate],
      ['Product',       item.product     || 'N/A', '80G Certificate', is80G ? 'Yes' : 'No'],
    ];
    y = drawTable(y, 'DONATION DETAILS', donationRows);

    y += 6;

    // ══════════════════════════════
    //  AMOUNT ROW (single full-width row)
    // ══════════════════════════════
    // Header
    pdf.setFillColor(...dkgray);
    pdf.rect(ML, y, TW, HDR_H, 'F');
    pdf.setTextColor(...white);
    pdf.setFontSize(8.5); pdf.setFont('helvetica', 'bold');
    pdf.text('DONATION AMOUNT', ML + CELL_PAD, y + 5.5);
    y += HDR_H;

    // Amount row body
    pdf.setFillColor(248, 248, 248);
    pdf.rect(ML, y, TW, 16, 'F');
    pdf.setDrawColor(...black); pdf.setLineWidth(0.3);
    pdf.rect(ML, y, TW, 16, 'S');

    setBlack();
    pdf.setFontSize(14); pdf.setFont('helvetica', 'bold');
    pdf.text(`Rs. ${amount.toLocaleString('en-IN')}`, ML + CELL_PAD, y + 10);

    pdf.setFontSize(8.5); pdf.setFont('helvetica', 'normal'); setDkGray();
    pdf.text(`In Words: ${amountInWords} Only`, W - MR - CELL_PAD, y + 10, { align: 'right' });

    y += 16 + 6;

    // ══════════════════════════════
    //  80G TAX EXEMPTION TABLE
    // ══════════════════════════════
    y = drawTable(y, '80G TAX EXEMPTION', [
      ['Approval No',  goshalaDetails.goshala80GApprovalNo,  'Approval Date', goshalaDetails.goshala80GApprovalDate],
      ['Valid Until',  goshalaDetails.goshala80GValidUntil,  'Note', 'Exempt u/s 80G of Income Tax Act, 1961'],
    ]);

    y += 10;

    // ══════════════════════════════
    //  SIGNATURE
    // ══════════════════════════════
    // Left: declaration text
    pdf.setFontSize(7.5); pdf.setFont('helvetica', 'italic'); setMdGray();
    pdf.text('Certified that the amount mentioned above has been received', ML + CELL_PAD, y);
    pdf.text('as donation and the donor is eligible for tax exemption.', ML + CELL_PAD, y + 5);

    // Right: signature line
    pdf.setDrawColor(...black); pdf.setLineWidth(0.4);
    pdf.line(W - MR - 55, y + 14, W - MR, y + 14);
    setBlack();
    pdf.setFontSize(8); pdf.setFont('helvetica', 'bold');
    pdf.text('Authorized Signatory', W - MR, y + 20, { align: 'right' });
    pdf.setFont('helvetica', 'normal'); setDkGray();
    pdf.text(goshalaDetails.authorizedSignatoryName,        W - MR, y + 26, { align: 'right' });
    pdf.text(goshalaDetails.authorizedSignatoryDesignation, W - MR, y + 31, { align: 'right' });

    // ── Close outer border ──
    pdf.setDrawColor(...black); pdf.setLineWidth(0.5);
    pdf.rect(ML, ML, TW, 269, 'S');

    // ── Footer line ──
    hline(ML + 269 + 3, 0.3);
    pdf.setFontSize(7); pdf.setFont('helvetica', 'normal'); setMdGray();
    pdf.text(`This is a system-generated receipt.   Receipt No: ${receiptNo}   Generated: ${receiptDate}`, W / 2, ML + 269 + 8, { align: 'center' });

    // ── Download IMMEDIATELY ──
    pdf.save(`Receipt_${(item.fullName || 'Donor').replace(/\s/g, '_')}_${receiptNo}.pdf`);
    toast.success('Receipt downloaded!');

    // ── Upload to Cloudinary & update Firestore in background (non-blocking) ──
    try {
      const blob = pdf.output('blob');
      const formData = new FormData();
      formData.append('file', blob);
      formData.append('upload_preset', uploadPreset);
      formData.append('folder', 'DonationReceipts');
      const response = await fetch(`https://api.cloudinary.com/v1_1/${cloudName}/auto/upload`, { method: 'POST', body: formData });
      const data = await response.json();
      if (data.secure_url) {
        await updateDoc(doc(firestore, 'DonateNow', item.id), { receiptUrl: data.secure_url, receiptNo, receiptDate });
      }
    } catch (e) {
      console.warn('Background receipt save failed:', e);
    }
  };

  const filteredDonations = donateList.filter(item => {
    let sevaDate = '';
    if (item.sevaDate) { const d = new Date(item.sevaDate); sevaDate = d.toISOString().split('T')[0]; }
    return (
      item.fullName?.toLowerCase().includes(filterName.toLowerCase()) &&
      item.mobileNumber?.includes(filterMobile) &&
      sevaDate.includes(filterDate) &&
      (filterCategory     === '' || item.category     === filterCategory) &&
      (filterProduct      === '' || item.product      === filterProduct) &&
      (filterDonationKind === '' || item.donationKind === filterDonationKind) &&
      (filterDonationType === '' ||
        (filterDonationType === 'monthly'  && item.donationType === 'monthly') ||
        (filterDonationType === 'one-time' && item.donationType !== 'monthly'))
    );
  });

  const totalPages  = Math.ceil(filteredDonations.length / ITEMS_PER_PAGE);
  const startIndex  = (currentPage - 1) * ITEMS_PER_PAGE;
  const currentData = filteredDonations.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  const handleFilterChange = (setter) => (e) => { setter(e.target.value); setCurrentPage(1); };
  const goToPage = (page) => { if (page >= 1 && page <= totalPages) setCurrentPage(page); };
  const getPageNumbers = () => {
    const pages = []; let start = Math.max(1, currentPage - 2); let end = Math.min(totalPages, start + 4);
    if (end - start < 4) start = Math.max(1, end - 4);
    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  };

  const isAnyFilterActive = filterName || filterMobile || filterDate || filterCategory || filterProduct || filterDonationKind || filterDonationType;

  // Stats
  const totalAmount = donateList.reduce((sum, d) => {
    const a = typeof d.donationAmount === 'string' ? parseInt(d.donationAmount.replace(/[₹,]/g, '')) : (d.donationAmount || 0);
    return sum + (isNaN(a) ? 0 : a);
  }, 0);
  const monthlyCount = donateList.filter(d => d.donationType === 'monthly').length;
  const uniqueDonors = new Set(donateList.map(d => d.mobileNumber).filter(Boolean)).size;

  if (isLoading) return <Loader />;

  return (
    <div className="dn-root lg:flex h-screen overflow-hidden" style={{ background: '#fafaf7' }}>
      <style>{styles}</style>
      <Sidebar isOpen={isSidebarOpen} toggleSidebar={setIsSidebarOpen} />

      <div className={`flex-1 flex flex-col overflow-hidden transition-all duration-300 ${isSidebarOpen ? 'lg:ml-56' : 'lg:ml-20'} mt-16 lg:mt-0`}>

        {/* ── Top Header Bar ── */}
        <div style={{ background: '#fff', borderBottom: '1.5px solid #fde68a', padding: '18px 28px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 36, height: 36, borderRadius: 10, background: 'linear-gradient(135deg, #fcaf17, #fb923c)', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                <img src={logoImg} alt="Logo" style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={e => { e.currentTarget.style.display = 'none'; }} />
              </div>
              <div>
                <h1 style={{ fontSize: 22, fontWeight: 800, color: '#1a1208', margin: 0, lineHeight: 1.1 }}>Donations</h1>
                <p style={{ fontSize: 12, color: '#a8927a', margin: 0, marginTop: 2 }}>Manage &amp; track all donation records</p>
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: '#78716c' }}>{filteredDonations.length} records</span>
          </div>
        </div>

        {/* ── Scrollable Content ── */}
        <div className="flex-1 overflow-auto" style={{ padding: '24px 28px' }}>

          {/* ── Stats Row ── */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginBottom: 24 }}>
            <StatCard icon={IndianRupee} label="Total Collected" value={`₹${totalAmount.toLocaleString('en-IN')}`} sub="Lifetime donations" color="linear-gradient(135deg,#fcaf17,#f59e0b)" />
            <StatCard icon={Users} label="Unique Donors" value={uniqueDonors} sub="Distinct contributors" color="linear-gradient(135deg,#fb923c,#ef4444)" />
            <StatCard icon={TrendingUp} label="Monthly Donors" value={monthlyCount} sub="Recurring supporters" color="linear-gradient(135deg,#34d399,#10b981)" />
            <StatCard icon={Heart} label="Total Records" value={donateList.length} sub="All-time entries" color="linear-gradient(135deg,#818cf8,#6366f1)" />
          </div>

          {/* ── Filter Panel ── */}
          <div className="dn-filter-panel">
            {/* Panel header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: showFilters ? 16 : 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <SlidersHorizontal size={15} color="#fcaf17" />
                <span style={{ fontSize: 13, fontWeight: 700, color: '#1a1208' }}>Filters</span>
                {isAnyFilterActive && (
                  <span style={{ background: '#fcaf17', color: '#fff', fontSize: 11, fontWeight: 700, padding: '1px 8px', borderRadius: 20 }}>
                    Active
                  </span>
                )}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                {isAnyFilterActive && (
                  <button className="dn-clear-btn active" onClick={handleRefresh}>
                    <RefreshCw size={13} /> Clear All
                  </button>
                )}
                <button className="dn-clear-btn inactive" onClick={() => setShowFilters(p => !p)}>
                  {showFilters ? 'Hide' : 'Show'} Filters
                </button>
              </div>
            </div>

            {showFilters && (
              <>
                {/* Row 1: text / date */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 12 }}>
                  <div>
                    <p className="dn-label">Name</p>
                    <div style={{ position: 'relative' }}>
                      <Search size={13} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#c4b89a' }} />
                      <input type="text" placeholder="Search name…" value={filterName} onChange={handleFilterChange(setFilterName)} className="dn-input" style={{ paddingLeft: 32 }} />
                    </div>
                  </div>
                  <div>
                    <p className="dn-label">Mobile</p>
                    <input type="text" placeholder="Search mobile…" value={filterMobile} onChange={handleFilterChange(setFilterMobile)} className="dn-input" />
                  </div>
                  <div>
                    <p className="dn-label">Seva Date</p>
                    <input type="date" value={filterDate} onChange={handleFilterChange(setFilterDate)} className="dn-input" />
                  </div>
                </div>

                {/* Row 2: dropdowns */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12 }}>
                  <div>
                    <p className="dn-label">Category</p>
                    <select value={filterCategory} onChange={handleFilterChange(setFilterCategory)} className="dn-select">
                      <option value="">All Categories</option>
                      {uniqueValues('category').map(v => <option key={v} value={v}>{v}</option>)}
                    </select>
                  </div>
                  <div>
                    <p className="dn-label">Product</p>
                    <select value={filterProduct} onChange={handleFilterChange(setFilterProduct)} className="dn-select">
                      <option value="">All Products</option>
                      {uniqueValues('product').map(v => <option key={v} value={v}>{v}</option>)}
                    </select>
                  </div>
                  <div>
                    <p className="dn-label">Donation Kind</p>
                    <select value={filterDonationKind} onChange={handleFilterChange(setFilterDonationKind)} className="dn-select">
                      <option value="">All Kinds</option>
                      {uniqueValues('donationKind').map(v => <option key={v} value={v}>{v}</option>)}
                    </select>
                  </div>
                  <div>
                    <p className="dn-label">Donation Type</p>
                    <select value={filterDonationType} onChange={handleFilterChange(setFilterDonationType)} className="dn-select">
                      <option value="">All Types</option>
                      <option value="monthly">Monthly</option>
                      <option value="one-time">One Time</option>
                    </select>
                  </div>
                </div>

                {/* Active chips */}
                {isAnyFilterActive && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 14, paddingTop: 14, borderTop: '1.5px solid #fde68a' }}>
                    {[
                      { label: filterName,         prefix: 'Name',     clear: () => { setFilterName('');         setCurrentPage(1); } },
                      { label: filterMobile,        prefix: 'Mobile',   clear: () => { setFilterMobile('');        setCurrentPage(1); } },
                      { label: filterDate,          prefix: 'Date',     clear: () => { setFilterDate('');          setCurrentPage(1); } },
                      { label: filterCategory,      prefix: 'Category', clear: () => { setFilterCategory('');      setCurrentPage(1); } },
                      { label: filterProduct,       prefix: 'Product',  clear: () => { setFilterProduct('');       setCurrentPage(1); } },
                      { label: filterDonationKind,  prefix: 'Kind',     clear: () => { setFilterDonationKind('');  setCurrentPage(1); } },
                      { label: filterDonationType === 'monthly' ? 'Monthly' : filterDonationType === 'one-time' ? 'One Time' : '', prefix: 'Type', clear: () => { setFilterDonationType(''); setCurrentPage(1); } },
                    ].filter(f => f.label).map((f, i) => (
                      <span key={i} className="dn-chip">
                        <span className="chip-label">{f.prefix}:</span> {f.label}
                        <button onClick={f.clear}><X size={11} /></button>
                      </span>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>

          {/* ── Table or Empty ── */}
          {filteredDonations.length > 0 ? (
            <>
              <div className="dn-table-wrap" style={{ overflowX: 'auto' }}>
                <table className="dn-table">
                  <thead>
                    <tr>
                      <th className="col-sticky-left" style={{ textAlign: 'center' }}>#</th>
                      <th>Full Name</th>
                      <th>Amount</th>
                      <th>Seva Date</th>
                      <th>Email</th>
                      <th>Mobile</th>
                      <th>Category</th>
                      <th>Donation Kind</th>
                      <th>Product</th>
                      <th>PAN</th>
                      <th>State</th>
                      <th>Type</th>
                      <th>80G</th>
                      <th>Prasadam</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentData.map((item, index) => (
                      <tr key={item.id}>
                        <td className="col-sticky-left" style={{ textAlign: 'center', fontWeight: 700, color: '#a8927a', fontSize: 12 }}>
                          {startIndex + index + 1}
                        </td>
                        <td style={{ fontWeight: 600, color: '#1a1208' }}>{item.fullName || '—'}</td>
                        <td className="amount-cell">
                          ₹{typeof item.donationAmount === 'string'
                            ? item.donationAmount.replace(/[₹,]/g, '')
                            : (item.donationAmount || '—')}
                        </td>
                        <td>{item.sevaDate ? new Date(item.sevaDate).toLocaleDateString('en-GB') : '—'}</td>
                        <td style={{ color: '#78716c' }}>{item.emailAddress || '—'}</td>
                        <td className="amount-cell">{item.mobileNumber || '—'}</td>
                        <td>{item.category      || '—'}</td>
                        <td>{item.donationKind  || '—'}</td>
                        <td>{item.product       || '—'}</td>
                        <td className="amount-cell" style={{ letterSpacing: '.04em' }}>{item.panNumber || '—'}</td>
                        <td>{item.state         || '—'}</td>
                        <td>
                          <span className={`dn-badge ${item.donationType === 'monthly' ? 'monthly' : 'onetime'}`}>
                            {item.donationType === 'monthly' ? 'Monthly' : 'One Time'}
                          </span>
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          <span className={`dn-badge ${parseInt(item.donationAmount) >= 500 && item.receive80GCertificate ? 'yes' : 'no'}`}>
                            {parseInt(item.donationAmount) >= 500 && item.receive80GCertificate ? 'Yes' : 'No'}
                          </span>
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          <span className={`dn-badge ${parseInt(item.donationAmount) >= 500 && item.receivePrasadam ? 'yes' : 'no'}`}>
                            {parseInt(item.donationAmount) >= 500 && item.receivePrasadam ? 'Yes' : 'No'}
                          </span>
                        </td>
                        <td>
                          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                            <button className="dn-action-btn whatsapp" onClick={() => handleWhatsAppMessage(item.mobileNumber, item.fullName, item.donationAmount, item)} title="Send WhatsApp">
                              <FaWhatsapp size={15} />
                            </button>
                            <button className="dn-action-btn receipt" onClick={() => generateReceiptPdf(item)} title="Download Receipt">
                              <FileText size={15} />
                            </button>
                            <button className="dn-action-btn delete" onClick={() => handleDeleteDonation(item.id)} title="Delete">
                              <Trash2 size={15} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 20, flexWrap: 'wrap', gap: 12 }}>
                  <p style={{ fontSize: 13, color: '#78716c', margin: 0 }}>
                    Showing <strong style={{ color: '#1a1208' }}>{startIndex + 1}</strong>–<strong style={{ color: '#1a1208' }}>{Math.min(startIndex + ITEMS_PER_PAGE, filteredDonations.length)}</strong> of <strong style={{ color: '#1a1208' }}>{filteredDonations.length}</strong> records
                  </p>
                  <div style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
                    <button className="dn-page-btn" onClick={() => goToPage(currentPage - 1)} disabled={currentPage === 1}>
                      <ChevronLeft size={13} /> Prev
                    </button>
                    {getPageNumbers()[0] > 1 && (
                      <>
                        <button className="dn-page-btn" onClick={() => goToPage(1)}>1</button>
                        {getPageNumbers()[0] > 2 && <span style={{ color: '#c4b89a', padding: '0 4px' }}>…</span>}
                      </>
                    )}
                    {getPageNumbers().map(page => (
                      <button key={page} className={`dn-page-btn ${page === currentPage ? 'active' : ''}`} onClick={() => goToPage(page)}>{page}</button>
                    ))}
                    {getPageNumbers().slice(-1)[0] < totalPages && (
                      <>
                        {getPageNumbers().slice(-1)[0] < totalPages - 1 && <span style={{ color: '#c4b89a', padding: '0 4px' }}>…</span>}
                        <button className="dn-page-btn" onClick={() => goToPage(totalPages)}>{totalPages}</button>
                      </>
                    )}
                    <button className="dn-page-btn" onClick={() => goToPage(currentPage + 1)} disabled={currentPage === totalPages}>
                      Next <ChevronRight size={13} />
                    </button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="dn-empty" style={{ background: '#fff', border: '1.5px solid #fde68a', borderRadius: 16 }}>
              <div className="dn-empty-icon">🙏</div>
              <p style={{ fontSize: 16, fontWeight: 700, color: '#1a1208', marginBottom: 6 }}>No records found</p>
              <p style={{ fontSize: 13, color: '#78716c' }}>No donation records match the current filters.</p>
              {isAnyFilterActive && (
                <button className="dn-clear-btn active" style={{ margin: '18px auto 0', display: 'inline-flex' }} onClick={handleRefresh}>
                  <RefreshCw size={13} /> Clear Filters
                </button>
              )}
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default DonateNow;